import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SurveyLanguage } from '@shared/types/surveyLanguage';
import { isSurveyLanguage, SURVEY_LANGUAGE_STORAGE_KEY } from '@shared/constants/surveyLanguage';
import { clearQ7_1Answers } from '@/lib/q7_1Storage';
import { clearQ7_2Answers } from '@/lib/q7_2Storage';
import { clearQ7_3Answers } from '@/lib/q7_3Storage';
import { clearQ7_4Answers } from '@/lib/q7_4Storage';
import { clearDevRegAnswers } from '@/lib/devregStorage';
import { clearTradeInAnswers } from '@/lib/tradeinStorage';
import { clearTradeInContactAnswers } from '@/lib/tradeinContactStorage';
import { clearSmokerProfileAnswers } from '@/lib/smokerProfileStorage';

function readStoredSurveyLanguage(): SurveyLanguage | null {
  try {
    const raw = sessionStorage.getItem(SURVEY_LANGUAGE_STORAGE_KEY);
    return isSurveyLanguage(raw) ? raw : null;
  } catch {
    return null;
  }
}

function persistSurveyLanguage(lang: SurveyLanguage | null) {
  try {
    if (lang === null) sessionStorage.removeItem(SURVEY_LANGUAGE_STORAGE_KEY);
    else sessionStorage.setItem(SURVEY_LANGUAGE_STORAGE_KEY, lang);
  } catch {
    /* private mode / quota */
  }
}

export const SURVEY_SCREENING_STORAGE_KEY = 'ploomsurvey:surveyScreening';

export type SurveySmokerAnswer = 'yes' | 'no';

/** Age band label + smoker answer saved when continuing past /age_smoker toward PDPA. */
export type SurveyScreeningAnswers = {
  ageBand: string;
  smoker: SurveySmokerAnswer;
};

function isSurveySmokerAnswer(v: unknown): v is SurveySmokerAnswer {
  return v === 'yes' || v === 'no';
}

function readStoredSurveyScreening(): SurveyScreeningAnswers | null {
  try {
    const raw = sessionStorage.getItem(SURVEY_SCREENING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { ageBand?: unknown }).ageBand !== 'string' ||
      !isSurveySmokerAnswer((parsed as { smoker?: unknown }).smoker)
    ) {
      return null;
    }
    return {
      ageBand: (parsed as SurveyScreeningAnswers).ageBand,
      smoker: (parsed as SurveyScreeningAnswers).smoker,
    };
  } catch {
    return null;
  }
}

function persistSurveyScreening(data: SurveyScreeningAnswers | null) {
  try {
    if (data === null) sessionStorage.removeItem(SURVEY_SCREENING_STORAGE_KEY);
    else sessionStorage.setItem(SURVEY_SCREENING_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export const PDPA_CONSENT_STORAGE_KEY = 'ploomsurvey:pdpaConsent';

function readStoredPdpaConsent(): boolean {
  try {
    return sessionStorage.getItem(PDPA_CONSENT_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistPdpaConsent(value: boolean) {
  try {
    sessionStorage.setItem(PDPA_CONSENT_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    /* ignore */
  }
}

export const UNIQUE_CODE_DRAFT_STORAGE_KEY = 'ploomsurvey:uniqueCodeDraft';

export type UniqueCodeDraft = {
  mobileLocalDigits: string;
  uniqueCode: string;
  /** Set after successful POST /api/v1/otp/request; required for OTP verification. */
  customerUuid?: string;
  /** `Date.now()` when OTP SMS was requested; used for verify delay UI (keep in sync with backend). */
  otpSentAtMs?: number;
  /** Set only when the backend returns an OTP (`OTP_SKIP_SMS`). Used to auto-fill /otp. */
  autofillOtp?: string;
};

function readStoredUniqueCodeDraft(): UniqueCodeDraft {
  try {
    const raw = sessionStorage.getItem(UNIQUE_CODE_DRAFT_STORAGE_KEY);
    if (!raw) return { mobileLocalDigits: '', uniqueCode: '' };
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return { mobileLocalDigits: '', uniqueCode: '' };
    }
    const o = parsed as {
      mobileLocalDigits?: unknown;
      uniqueCode?: unknown;
      customerUuid?: unknown;
      otpSentAtMs?: unknown;
      autofillOtp?: unknown;
    };
    const otpSentAtMs =
      typeof o.otpSentAtMs === 'number' && Number.isFinite(o.otpSentAtMs) ? o.otpSentAtMs : undefined;
    const autofillOtp =
      typeof o.autofillOtp === 'string' && /^\d{4,8}$/.test(o.autofillOtp) ? o.autofillOtp : undefined;
    return {
      mobileLocalDigits: typeof o.mobileLocalDigits === 'string' ? o.mobileLocalDigits : '',
      uniqueCode: typeof o.uniqueCode === 'string' ? o.uniqueCode : '',
      customerUuid: typeof o.customerUuid === 'string' ? o.customerUuid : undefined,
      ...(otpSentAtMs !== undefined ? { otpSentAtMs } : {}),
      ...(autofillOtp !== undefined ? { autofillOtp } : {}),
    };
  } catch {
    return { mobileLocalDigits: '', uniqueCode: '' };
  }
}

function persistUniqueCodeDraft(data: UniqueCodeDraft) {
  try {
    sessionStorage.setItem(UNIQUE_CODE_DRAFT_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

const appSlice = createSlice({
  name: 'app',
  initialState: {
    ready: true as boolean,
    surveyLanguage: readStoredSurveyLanguage(),
    surveyScreening: readStoredSurveyScreening() as SurveyScreeningAnswers | null,
    pdpaConsent: readStoredPdpaConsent(),
    uniqueCodeDraft: readStoredUniqueCodeDraft(),
  },
  reducers: {
    setSurveyLanguage(state, action: PayloadAction<SurveyLanguage>) {
      state.surveyLanguage = action.payload;
      persistSurveyLanguage(action.payload);
    },
    clearSurveyLanguage(state) {
      state.surveyLanguage = null;
      persistSurveyLanguage(null);
    },
    setSurveyScreeningAnswers(state, action: PayloadAction<SurveyScreeningAnswers>) {
      state.surveyScreening = action.payload;
      persistSurveyScreening(action.payload);
    },
    clearSurveyScreeningAnswers(state) {
      state.surveyScreening = null;
      persistSurveyScreening(null);
      state.pdpaConsent = false;
      persistPdpaConsent(false);
      state.uniqueCodeDraft = { mobileLocalDigits: '', uniqueCode: '' };
      persistUniqueCodeDraft({ mobileLocalDigits: '', uniqueCode: '' });
      clearSmokerProfileAnswers();
      clearQ7_1Answers();
      clearQ7_2Answers();
      clearQ7_3Answers();
      clearQ7_4Answers();
      clearDevRegAnswers();
      clearTradeInAnswers();
      clearTradeInContactAnswers();
    },
    setPdpaConsent(state, action: PayloadAction<boolean>) {
      state.pdpaConsent = action.payload;
      persistPdpaConsent(action.payload);
    },
    setUniqueCodeDraft(state, action: PayloadAction<UniqueCodeDraft>) {
      state.uniqueCodeDraft = action.payload;
      persistUniqueCodeDraft(action.payload);
    },
    /** In-memory wipe for a new respondent; pair with clearing web storage (caller). */
    resetAppForNewCustomer(state) {
      state.surveyLanguage = null;
      state.surveyScreening = null;
      state.pdpaConsent = false;
      state.uniqueCodeDraft = { mobileLocalDigits: '', uniqueCode: '' };
    },
  },
});

export const {
  setSurveyLanguage,
  clearSurveyLanguage,
  setSurveyScreeningAnswers,
  clearSurveyScreeningAnswers,
  setPdpaConsent,
  setUniqueCodeDraft,
  resetAppForNewCustomer,
} = appSlice.actions;

export default appSlice.reducer;
